<template>
  <ModalSheet :aria-label="title" :hidden="hidden" :panel-class="panelClass" :body-class="bodyClass">
    <template #header>
      <div class="px-4 pt-5 md:px-6 md:pt-6">
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="text-xl font-bold text-gray-brand-900 tracking-tighter">
              {{ title }}
            </div>
            <h3 class="mt-1 text-md text-gray-brand-700">
              {{ SUBTITLE }}
            </h3>
          </div>
          <button
            data-testid="upsell-modal-close"
            type="button"
            aria-label="Close"
            class="flex-shrink-0 rounded-full p-1.5 text-gray-brand-700 hover:bg-gray-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-900"
            @click="$emit('close')"
          >
            <XIcon class="h-5 w-5" />
          </button>
        </div>

        <!-- Mark's design gives the pricing-tier progress its own card. `empty:hidden` collapses it
             when the store has no tier to show - which is why the views pass the progress with
             `v-if` rather than `v-show`: a display:none child still counts for CSS `:empty` and
             would leave an empty bordered box behind. -->
        <!-- prettier-ignore -->
        <div
          v-if="$slots.progress"
          data-testid="upsell-modal-progress"
          class="mt-4 empty:hidden rounded-lg border border-gray-brand-300 bg-gray-brand-100 px-3 py-2.5"
        ><slot name="progress" /></div>
      </div>
    </template>

    <slot />

    <template #footer>
      <div class="flex items-center justify-between gap-3 mb-3">
        <div class="flex items-center gap-2 text-gray-brand-900">
          <ShoppingCartIcon class="h-4 w-4 text-gray-brand-500" />
          <span v-if="totalFormatted" class="font-semibold">{{ totalFormatted }}</span>
          <span class="text-sm text-gray-brand-600">{{ itemCountLabel }}</span>
        </div>
        <button
          data-testid="upsell-modal-dismiss"
          type="button"
          class="text-sm text-gray-brand-700 underline hover:text-gray-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-900 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="proceedDisabled || submitting"
          @click="$emit('proceed')"
        >
          {{ dismissLabel }}
        </button>
      </div>
      <PrimaryButton
        data-testid="upsell-modal-proceed"
        class="w-full"
        :size="'lg'"
        :active="!proceedDisabled"
        :is-loading="submitting"
        justify="justify-between"
        @click="$emit('proceed')"
      >
        <!-- Invisible left spacer so the label reads centred against the right-hand arrow. -->
        <template #left-icon><ArrowRightIcon class="h-4 invisible" /></template>
        {{ proceedLabel }}
        <template #right-icon><ArrowRightIcon class="h-4" /></template>
      </PrimaryButton>
    </template>
  </ModalSheet>
</template>

<script>
import { ArrowRightIcon, ShoppingCartIcon, XIcon } from '@heroicons/vue/solid'
import ModalSheet from '@/components/Molecules/ModalSheet.vue'
import PrimaryButton from '@/components/Molecules/PrimaryButton'

// The mockups pair the header with a warmer second line. There is no merchant field behind it
// (categories carry only upsell_header_text and upsell_button_text), so it is the same for everyone.
const SUBTITLE = 'A few add-ons that go great with your order'

export default {
  name: 'UpsellModal',

  components: {
    ArrowRightIcon,
    ModalSheet,
    PrimaryButton,
    ShoppingCartIcon,
    XIcon
  },

  props: {
    // The merchant's upsell_header_text. Most merchants have customised it, so it wins over the
    // design's copy whenever it is set.
    headerText: {
      type: String,
      default: ''
    },
    // The merchant's upsell_button_text, which used to label the basket button and now labels the
    // dismiss link.
    buttonText: {
      type: String,
      default: ''
    },
    totalFormatted: {
      type: String,
      default: ''
    },
    itemCount: {
      type: Number,
      default: 0
    },
    proceedDisabled: {
      type: Boolean,
      default: false
    },
    // Set while the product modal is open on top of this one. Hidden, not unmounted, so the
    // customer comes back to the same place in the list.
    hidden: {
      type: Boolean,
      default: false
    },
    // 'list' is one product per full-width row (the default, and what scales to the 7 upsells the
    // busiest stores configure). 'grid' is Mark's 2x2.
    layout: {
      type: String,
      default: 'list',
      validator: (value) => ['list', 'grid'].includes(value)
    },
    submitting: {
      type: Boolean,
      default: false
    },
    proceedLabel: {
      type: String,
      default: 'Continue to checkout'
    }
  },

  emits: ['proceed', 'close'],

  // The product cards inject this to swap the store's half-width grid tile for a full-width row,
  // mirroring how QuestionModal provides inQuestionModal.
  provide() {
    return {
      inUpsellModal: true,
      // Read by useProductCardLayout to size each card as a full-width row or a 2x2 tile.
      upsellModalLayout: this.layout
    }
  },

  computed: {
    // Both layouts want more room than a default dialog: these rows carry a thumbnail, a
    // description, a price with a strike-through and a stepper, and the variant-picker rows expand
    // underneath. Two columns need more again - at the list's width a tile lands around 230px,
    // which wraps product titles onto three lines and squeezes the dropdowns.
    panelClass() {
      return this.layout === 'grid' ? 'md:max-w-3xl' : 'md:max-w-2xl'
    },
    // A 2x2 needs the body to lay its children out in a wrapping row; the list stacks them.
    // The header block has no bottom padding of its own, so the top padding here is what keeps the
    // first upsell off the progress card - or off the subtitle when there is no tier to show.
    bodyClass() {
      const base = 'px-4 pt-4 pb-4 md:px-6'
      return this.layout === 'grid' ? `${base} flex flex-wrap items-stretch` : base
    },
    title() {
      return this.headerText || 'Before you go…'
    },
    dismissLabel() {
      return this.buttonText || 'No thanks'
    },
    itemCountLabel() {
      return `${this.itemCount} ${this.itemCount === 1 ? 'item' : 'items'}`
    }
  },

  created() {
    this.SUBTITLE = SUBTITLE
  }
}
</script>
